import { NgStyle } from '@angular/common';
import {
  Component,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import {
  GuidedTourAudience,
  GuidedTourRect,
  GuidedTourStep,
} from '../../models/guide/guided-tour.model';
import { markAllToursDisabled, markTourCompleted } from '../../utils/guided-tour-state.util';

const SPOTLIGHT_PADDING = 8;
const CARD_GAP = 14;
const NAVIGATION_DELAY_MS = 450;

@Component({
  selector: 'app-guided-tour',
  imports: [NgStyle, ButtonModule],
  templateUrl: './guided-tour.html',
  styleUrl: './guided-tour.css',
})
export class GuidedTour {
  private readonly router = inject(Router);

  readonly audience = input.required<GuidedTourAudience>();
  readonly visible = input(false);
  readonly steps = input.required<GuidedTourStep[]>();

  readonly visibleChange = output<boolean>();
  readonly completed = output<void>();

  readonly currentStepIndex = signal(0);
  readonly targetRect = signal<GuidedTourRect | null>(null);
  readonly syncing = signal(false);

  private lastVisible = false;
  private syncToken = 0;

  readonly currentStep = computed(() => this.steps()[this.currentStepIndex()] ?? null);

  constructor() {
    effect(() => {
      const isVisible = this.visible();
      if (isVisible && !this.lastVisible) {
        this.currentStepIndex.set(0);
      }
      this.lastVisible = isVisible;

      if (!isVisible) {
        this.targetRect.set(null);
        return;
      }

      const index = this.currentStepIndex();
      const steps = this.steps();
      void this.syncStep(index, steps);
    });
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.visible() && !this.syncing()) {
      this.refreshTargetRect();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible()) {
      this.skipForNow();
    }
  }

  isFirstStep(): boolean {
    return this.currentStepIndex() === 0;
  }

  isLastStep(): boolean {
    return this.currentStepIndex() >= this.steps().length - 1;
  }

  spotlightStyle(): Record<string, string> {
    const rect = this.targetRect();
    if (!rect) return {};

    return {
      top: `${Math.max(rect.top - SPOTLIGHT_PADDING, 0)}px`,
      left: `${Math.max(rect.left - SPOTLIGHT_PADDING, 0)}px`,
      width: `${rect.width + SPOTLIGHT_PADDING * 2}px`,
      height: `${rect.height + SPOTLIGHT_PADDING * 2}px`,
    };
  }

  cardStyle(): Record<string, string> {
    const rect = this.targetRect();
    const step = this.currentStep();
    if (!rect || !step) return {};

    const cardWidth = Math.min(352, window.innerWidth - 32);
    const cardHeight = 240;
    const placement = step.placement ?? 'auto';

    let top = rect.top + rect.height + SPOTLIGHT_PADDING + CARD_GAP;
    let left = rect.left + rect.width / 2 - cardWidth / 2;

    if (placement === 'top' || (placement === 'auto' && top + cardHeight > window.innerHeight - 16)) {
      top = rect.top - SPOTLIGHT_PADDING - CARD_GAP - cardHeight;
    }

    if (placement === 'left') {
      top = rect.top + rect.height / 2 - cardHeight / 2;
      left = rect.left - CARD_GAP - cardWidth;
    }

    if (placement === 'right') {
      top = rect.top + rect.height / 2 - cardHeight / 2;
      left = rect.left + rect.width + CARD_GAP;
    }

    top = Math.min(Math.max(top, 16), window.innerHeight - cardHeight - 16);
    left = Math.min(Math.max(left, 16), window.innerWidth - cardWidth - 16);

    return {
      top: `${top}px`,
      left: `${left}px`,
      width: `${cardWidth}px`,
    };
  }

  next(): void {
    if (this.isLastStep() || this.syncing()) return;
    this.currentStepIndex.update(index => index + 1);
  }

  previous(): void {
    if (this.isFirstStep() || this.syncing()) return;
    this.currentStepIndex.update(index => index - 1);
  }

  skipForNow(): void {
    this.close();
  }

  dontShowAgain(): void {
    markAllToursDisabled();
    markTourCompleted(this.audience());
    this.completed.emit();
    this.close();
  }

  finish(): void {
    markTourCompleted(this.audience());
    this.completed.emit();
    this.close();
  }

  private close(): void {
    this.currentStepIndex.set(0);
    this.targetRect.set(null);
    this.visibleChange.emit(false);
  }

  private async syncStep(index: number, steps: GuidedTourStep[]): Promise<void> {
    const step = steps[index];
    const token = ++this.syncToken;
    if (!step) {
      this.targetRect.set(null);
      return;
    }

    this.syncing.set(true);
    this.targetRect.set(null);

    if (step.route) {
      const currentPath = this.router.url.split('?')[0];
      if (currentPath !== step.route) {
        await this.router.navigateByUrl(step.route);
        await this.delay(NAVIGATION_DELAY_MS);
      }
    }

    if (token !== this.syncToken) return;

    this.refreshTargetRect();
    this.syncing.set(false);
  }

  private refreshTargetRect(): void {
    const step = this.currentStep();
    if (!step) {
      this.targetRect.set(null);
      return;
    }

    const element = document.querySelector(`[data-tour="${step.target}"]`);
    if (!(element instanceof HTMLElement)) {
      this.targetRect.set(null);
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

    window.setTimeout(() => {
      const rect = element.getBoundingClientRect();
      this.targetRect.set({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }, 220);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }
}
